import { EffectSchema, defaultEffect, effectKind, type Effect } from '@flobi/artwork-core';

/**
 * What each effect looks like on its tile, and so what a press on that tile adds (option B of five,
 * 16 Oct). The engine's defaults are made for working at full size — a 4 px blur, a colour balance
 * of nought — and on a 70 px tile they read as no effect at all, so a look is measured from the
 * layer's shorter side and the colour effects start somewhere visible. What the tile shows is what
 * lands: the same values go to the layer.
 */
const LOOKS: Partial<Record<Effect['type'], (side: number) => Record<string, unknown>>> = {
  'drop-shadow': (side) => ({ y: side * 0.025, blur: side * 0.06, color: { r: 0, g: 0, b: 0, a: 0.4 } }),
  'inner-shadow': (side) => ({ y: side * 0.02, blur: side * 0.06, color: { r: 0, g: 0, b: 0, a: 0.5 } }),
  'layer-blur': (side) => ({ radius: side * 0.04 }),
  'motion-blur': (side) => ({ radius: side * 0.1 }),
  'background-blur': (side) => ({ radius: side * 0.04 }),
  hsb: () => ({ saturation: 1.45, contrast: 1.08 }),
  'color-balance': () => ({ red: 0.12, green: 0.03, blue: -0.12 }),
  noise: (side) => ({ amount: 0.3, scale: side * 0.004 }),
  sharpen: (side) => ({ amount: 0.7, radius: side * 0.003 }),
  bloom: (side) => ({ radius: side * 0.05, intensity: 0.9 }),
  glitch: (side) => ({ amount: side * 0.06, bands: side * 0.04 }),
  'chromatic-aberration': (side) => ({ amount: side * 0.01 }),
};

/** The names the tiles wear where the engine's would not fit four across. */
export const TILE_NAMES: Partial<Record<Effect['type'], string>> = {
  'layer-blur': 'Blur',
  'background-blur': 'Frosted',
  hsb: 'Adjust Colour',
  'color-balance': 'Balance',
  'chromatic-aberration': 'RGB Split',
};

/** The effect a tile shows on a layer whose shorter side is `side`, every number inside its field's range. */
export function effectLook(type: Effect['type'], side: number): Effect | null {
  const base = defaultEffect(type);
  const look = LOOKS[type];
  if (!base || !look || !(side > 0)) return base;
  const fields = effectKind(type)?.fields ?? [];
  const values = look(side);
  for (const field of fields) {
    const value = values[field.key];
    if (typeof value !== 'number') continue;
    // On the field's own step, so the number shown beside the slider is one it could have set.
    values[field.key] = Math.min(field.max, Math.max(field.min, Math.round(value / field.step) * field.step));
  }
  const parsed = EffectSchema.safeParse({ ...base, ...values });
  return parsed.success ? parsed.data : base;
}
